import storage from '../../../hooks/useStorage/useStorage';
import { useAppEnvironmentStore } from '../../../stores/AppEnvironment/appEnvironment.store';
import { RachelPageContext } from '../rachelPageContext.types';
import { useRachelStore } from '../store/rachel.store';
import {
  GetReplacementPageContextParams,
  HasNoPageContextToDetachParams,
  PendingPageContextDetach,
  ShouldDetachDismissedPageContextParams,
} from './useRachelChat.utils.types';

export const isSameAttachedPage = (
  attached: RachelPageContext | null,
  current: RachelPageContext,
): boolean =>
  attached?.pathname === current.pathname &&
  attached?.entity === current.entity &&
  attached?.entityId === current.entityId &&
  (attached?.focusedEntity ?? null) === (current.focusedEntity ?? null) &&
  (attached?.focusedEntityId ?? null) === (current.focusedEntityId ?? null);

export const getReplacementPageContext = ({
  activeChatId,
  hasLoadedAttachedPageContext,
  attachedPageContext,
  currentPageContext,
  isPageContextDismissed,
}: GetReplacementPageContextParams): RachelPageContext | null => {
  if (
    isPageContextDismissed ||
    !activeChatId ||
    !hasLoadedAttachedPageContext ||
    isSameAttachedPage(attachedPageContext, currentPageContext)
  ) {
    return null;
  }

  return currentPageContext;
};

export const shouldDetachDismissedPageContext = ({
  activeChatId,
  hasLoadedAttachedPageContext,
  attachedPageContext,
  isPageContextDismissed,
}: ShouldDetachDismissedPageContextParams): boolean =>
  isPageContextDismissed && !!activeChatId && hasLoadedAttachedPageContext && !!attachedPageContext;

export const hasNoPageContextToDetach = ({
  hasLoadedAttachedPageContext,
  attachedPageContext,
}: HasNoPageContextToDetachParams): boolean => hasLoadedAttachedPageContext && !attachedPageContext;

export const isPageContextDismissedNow = (pageContext: RachelPageContext): boolean =>
  isSameAttachedPage(useRachelStore.getState().dismissedPageContext, pageContext);

export const waitForPendingDetach = async (
  pendingDetach: PendingPageContextDetach | null,
  chatId: string,
): Promise<void> => {
  if (pendingDetach?.chatId !== chatId) {
    return;
  }
  await pendingDetach.promise.catch(() => undefined);
};

export const getRachelRequestHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};

  const sessionId = storage.getSessionId();
  const teamId = storage.getTeamId();
  const adminTeamId = storage.getAdminTeamId() as string | undefined;
  const brandId = storage.getBrandId();
  const appEnvironment = useAppEnvironmentStore.getState().appEnvironment;

  if (sessionId) {
    headers['x-session-id'] = sessionId;
  }
  if (teamId) {
    headers['x-team-id'] = teamId;
  }
  if (adminTeamId) {
    headers['x-admin-team-id'] = adminTeamId;
  }
  if (brandId) {
    headers['x-brand-id'] = brandId;
  }
  if (appEnvironment) {
    headers['x-app-environment'] = appEnvironment;
  }

  return headers;
};
